/**
 * The case-list dashboard — the agent's view of the requests they have filed.
 *
 * The read-path counterpart to the request wizard: it reads one page of cases from the V3
 * Web API through the cases-list query (api/caseListApi.ts) and renders it, with the four
 * states a remote read has — loading, error, empty, and a page of results — each given its
 * own surface rather than collapsed into one.
 *
 * Paging is opaque and forward/back only: the query is keyed on the continuation token the
 * paging slice holds (store/caseListSlice.ts), and Next/Previous move through the tokens
 * CMS minted without this view ever parsing one. An agent who has filed nothing is not an
 * error — it is an empty page with a plain, reassuring message.
 */
import { useEffect, useState } from 'react';
import {
  Body1,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  Title2,
  Button,
  makeStyles,
  shorthands,
  tokens,
} from '@fluentui/react-components';
import { ArrowLeft20Regular, ArrowRight20Regular } from '@fluentui/react-icons';
import { Alert, AlertDescription, Badge } from '@/fluent-adapter';
import { useAppDispatch, useAppSelector } from '@/store';
import { useListCasesQuery } from '../../api/caseListApi';
import type { CaseListItem } from '../../api/contracts';
import { isRetryable } from '../../api/errors';
import { advancePage, retreatPage, resetPaging } from '../../store/caseListSlice';
import { emptyStateKind, humanizeEnum, statusText } from '../../utils/caseListDisplay';

const useStyles = makeStyles({
  page: {
    maxWidth: '1080px',
    marginLeft: 'auto',
    marginRight: 'auto',
    ...shorthands.padding(tokens.spacingVerticalXXL, tokens.spacingHorizontalL),
    display: 'flex',
    flexDirection: 'column',
    rowGap: tokens.spacingVerticalL,
  },
  header: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: tokens.spacingVerticalXS,
  },
  muted: { color: tokens.colorNeutralForeground3 },
  centered: {
    display: 'flex',
    justifyContent: 'center',
    ...shorthands.padding(tokens.spacingVerticalXXXL, tokens.spacingHorizontalL),
  },
  empty: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    rowGap: tokens.spacingVerticalS,
    ...shorthands.padding(tokens.spacingVerticalXXXL, tokens.spacingHorizontalL),
    textAlign: 'center',
  },
  footer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: tokens.spacingVerticalM,
  },
  pager: {
    display: 'flex',
    columnGap: tokens.spacingHorizontalS,
  },
  actions: {
    display: 'flex',
    justifyContent: 'flex-start',
  },
  wrap: { overflowWrap: 'anywhere' },
});

/** Renders an ISO instant as a short, locale-aware date, leaving a bad value untouched. */
function formatDate(iso: string | null | undefined): string {
  // The wire value is nullable and ASP.NET sends an absent date as `null`, so `null` — not
  // just `undefined` — is the "no date" case; treating only `undefined` as absent would make
  // `new Date(null)` render the Unix epoch (PORT-ERROR).
  if (iso == null) {
    return '—';
  }

  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) {
    return iso;
  }

  return parsed.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

/** Renders an optional enum wire value humanized, or a dash when absent. */
function enumOrDash(value: string | null | undefined): string {
  return value == null || value === '' ? '—' : humanizeEnum(value);
}

/** Display names for request types the generic humanizer would mangle ("Epoc Pr"). */
const REQUEST_TYPE_LABELS: Record<string, string> = {
  epoc: 'EPOC',
  epocPr: 'EPOC-PR',
  unknown: 'Unknown',
};

/** Renders a request type code, falling back to the humanizer for codes not listed. */
function requestTypeLabel(value: string): string {
  return REQUEST_TYPE_LABELS[value] ?? enumOrDash(value);
}

/**
 * Renders the disclosure summary, or a dash when there is none. Cancelled and invalid cases
 * hide disclosure data even when the server sends it (list contract rule).
 */
function disclosureText(item: CaseListItem): string {
  if (item.disclosure == null || item.status === 'cancelled' || item.status === 'invalid') {
    return '—';
  }

  const { packagesDisclosed } = item.disclosure;
  return `${packagesDisclosed} package${packagesDisclosed === 1 ? '' : 's'} disclosed`;
}

interface CaseListViewProps {
  /** Returns to the shell's landing screen. */
  onExit: () => void;
}

export function CaseListView({ onExit }: CaseListViewProps) {
  const styles = useStyles();
  const dispatch = useAppDispatch();
  const { pageSize, currentToken, history } = useAppSelector((state) => state.caseList);

  // Paging resets to the first page when the dashboard is (re-)entered, and the query is
  // held until that reset has landed. Effects run after render, so without the gate the
  // query would fire once with a continuation token left in the slice from a previous visit
  // — reopening mid-list on a set that may have changed — and then again after the reset.
  // A mount-scoped flag is used deliberately rather than a token-derived predicate, so an
  // in-view Next/Previous (which does change currentToken) is not mistaken for a re-entry
  // and does not reset paging.
  const [pagingReady, setPagingReady] = useState(false);
  useEffect(() => {
    dispatch(resetPaging());
    setPagingReady(true);
  }, [dispatch]);

  const { data, isFetching, isError, error, refetch } = useListCasesQuery(
    {
      pageSize,
      continuationToken: currentToken,
    },
    // Skip until the mount reset lands so no request goes out on a stale token; then a
    // re-entry — for instance right after filing a case — refetches rather than serving a
    // page RTK Query cached on an earlier visit. List and create are separate API slices,
    // so the create mutation cannot invalidate this cache; the mount refetch is what keeps a
    // just-filed case from looking lost (PORT-PARITY with the write path's freshness
    // expectation).
    { skip: !pagingReady, refetchOnMountOrArgChange: true },
  );

  const canGoBack = history.length > 0;
  const canGoForward = data?.hasMoreResults === true && data.continuationToken != null;
  const emptyState =
    !isFetching && !isError && data !== undefined ? emptyStateKind(data, history.length) : undefined;

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <Title2>Data requests</Title2>
        <span className={styles.muted}>
          The law-enforcement requests you have filed, newest first.
        </span>
      </div>

      {/* The query is skipped until the mount paging reset lands (see below), during which
          isFetching is false and there is no data yet; treat that pre-effect window as
          loading so the view shows the spinner rather than a blank frame (PORT-ERROR: a
          transient state is not an empty result). */}
      {(isFetching || !pagingReady) && (
        <div className={styles.centered}>
          <Spinner label="Loading your cases…" />
        </div>
      )}

      {!isFetching && isError && (
        <Alert variant="destructive">
          <AlertDescription>
            {error?.message ?? 'Your cases could not be loaded. Please try again shortly.'}
          </AlertDescription>
          {isRetryable(error?.code) && (
            <div className={styles.actions} style={{ marginTop: tokens.spacingVerticalS }}>
              <Button appearance="primary" onClick={() => void refetch()}>
                Try again
              </Button>
            </div>
          )}
        </Alert>
      )}

      {emptyState === 'moreResults' && (
        <div className={styles.empty}>
          <Body1>No cases on this page.</Body1>
          <span className={styles.muted}>Select Next to see more cases.</span>
        </div>
      )}

      {emptyState === 'firstPage' && (
        <div className={styles.empty}>
          <Body1>You have not filed any cases yet.</Body1>
          <span className={styles.muted}>
            Cases you submit through the portal will appear here.
          </span>
        </div>
      )}

      {emptyState === 'pastEnd' && (
        <div className={styles.empty}>
          <Body1>No more cases to show on this page.</Body1>
          <span className={styles.muted}>
            The list may have changed since you opened it. Use Previous to step back.
          </span>
        </div>
      )}

      {!isFetching && !isError && data !== undefined && data.items.length > 0 && (
        <Table aria-label="Data requests" size="medium">
          <TableHeader>
            <TableRow>
              {/* Minimal columns for the E-Evidence list contract, so the page reads the new
                  rows rather than going blank. The full Data requests design (search,
                  filters, final labels) replaces this table. */}
              <TableHeaderCell>Case ID</TableHeaderCell>
              <TableHeaderCell>Request type</TableHeaderCell>
              <TableHeaderCell>LE reference</TableHeaderCell>
              <TableHeaderCell>Priority</TableHeaderCell>
              <TableHeaderCell>Received</TableHeaderCell>
              <TableHeaderCell>Status</TableHeaderCell>
              <TableHeaderCell>Disclosure</TableHeaderCell>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.items.map((item) => (
              <TableRow key={item.caseId}>
                <TableCell>{item.caseId}</TableCell>
                <TableCell>{requestTypeLabel(item.requestType)}</TableCell>
                <TableCell className={styles.wrap}>{item.leReferenceNumber ?? 'Not available yet'}</TableCell>
                <TableCell>{enumOrDash(item.productionDeadline)}</TableCell>
                <TableCell>{formatDate(item.receivedAt)}</TableCell>
                <TableCell>
                  {item.status !== '' ? (
                    <Badge variant="secondary">{statusText(item)}</Badge>
                  ) : (
                    '—'
                  )}
                </TableCell>
                <TableCell>{disclosureText(item)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      {/* The pager is independent of the current page's fullness: Previous is offered
          whenever there is history to step back through, and it is also shown when the
          current page is empty but a next token exists — an empty page carrying more results
          must not strand the agent with no way forward — so the guard admits canGoForward as
          well. Next follows the token rather than the row count. */}
      {!isFetching && !isError && data !== undefined && (data.items.length > 0 || canGoBack || canGoForward) && (
        <div className={styles.footer}>
          <span className={styles.muted}>
            {data.items.length === 0
              ? ''
              : data.totalCount != null
                ? `${data.totalCount} request${data.totalCount === 1 ? '' : 's'} total`
                : `${data.items.length} on this page`}
          </span>
          <div className={styles.pager}>
            <Button
              appearance="secondary"
              icon={<ArrowLeft20Regular />}
              disabled={!canGoBack}
              onClick={() => dispatch(retreatPage())}
            >
              Previous
            </Button>
            <Button
              appearance="secondary"
              iconPosition="after"
              icon={<ArrowRight20Regular />}
              disabled={!canGoForward}
              onClick={() => {
                if (data.continuationToken != null) {
                  dispatch(advancePage(data.continuationToken));
                }
              }}
            >
              Next
            </Button>
          </div>
        </div>
      )}

      <div className={styles.actions}>
        <Button appearance="subtle" onClick={onExit}>
          Back to portal
        </Button>
      </div>
    </div>
  );
}
